import React from "react";
import { COLORS, tint, StateBadge, LONG_TEXT_STYLE } from "../../../components/palette";

/* ====================================================================== */
/* Piezas reutilizables (también las usa el Dashboard)                     */
/* ====================================================================== */

export function KpiTile({ label, value, sub, col = "col-6 col-md-4 col-xl", children }) {
	return (
		<div className={`${col} mb-4`}>
			<div className="card card-custom bg-light h-100">
				<div className="card-body py-5 px-6">
					<div className="text-muted font-size-sm">{label}</div>
					<div
						className="font-size-h2 font-weight-bolder"
						style={{ color: COLORS.blue, lineHeight: 1.2 }}
					>
						{value}
					</div>
					{sub && <div className="text-muted font-size-sm mt-1">{sub}</div>}
					{children}
				</div>
			</div>
		</div>
	);
}

export function ProgressBar({ value, color = COLORS.blue, height = 6, className }) {
	const v = Math.max(0, Math.min(100, value || 0));
	return (
		<div className={`progress ${className || ""}`} style={{ height: `${height}px` }}>
			<div
				className="progress-bar"
				role="progressbar"
				style={{ width: `${v}%`, backgroundColor: color }}
				aria-valuenow={v}
				aria-valuemin="0"
				aria-valuemax="100"
			/>
		</div>
	);
}

/** Distribución por opción de un dato clave: recuento, % y opción correcta marcada. */
export function OptionBars({ stats }) {
	if (!stats.options.length)
		return <div className="text-muted font-size-sm">Sin opciones definidas.</div>;
	return (
		<table className="table table-sm table-vertical-center mb-0">
			<thead>
				<tr>
					<th className="border-top-0">Opción</th>
					<th className="border-top-0 text-right" style={{ width: "70px" }}>
						Mesas
					</th>
					<th className="border-top-0" style={{ width: "45%" }}>
						%
					</th>
				</tr>
			</thead>
			<tbody>
				{stats.rows.map(r => (
					<tr
						key={r.option}
						style={r.correct ? { backgroundColor: tint(COLORS.green, 0.12) } : undefined}
					>
						<td>
							{r.correct ? (
								<span className="font-weight-bolder" style={{ color: COLORS.green }}>
									✔ {r.option}
								</span>
							) : (
								r.option
							)}
						</td>
						<td className="text-right font-weight-bolder">{r.count}</td>
						<td>
							<div className="d-flex align-items-center">
								<ProgressBar
									value={r.pct}
									color={r.correct ? COLORS.green : COLORS.blue}
									className="flex-grow-1 mr-2"
								/>
								<span className="font-size-sm" style={{ minWidth: "36px" }}>
									{r.pct} %
								</span>
							</div>
						</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

/** Tarjeta de un dato clave (apertura o eje): pregunta, mesas que han respondido, reparto y acierto. */
export function SurpriseSummaryCard({ title, stats, tablesCount, revealed }) {
	return (
		<div className="card card-custom card-border h-100">
			<div className="card-body py-5 px-6">
				<div className="d-flex align-items-center justify-content-between mb-2">
					<h6 className="font-weight-bolder mb-0">{title}</h6>
					{revealed ? (
						<StateBadge color={COLORS.green}>revelado</StateBadge>
					) : (
						<StateBadge color={COLORS.gray}>pendiente</StateBadge>
					)}
				</div>
				<div className="font-weight-bold mb-2" style={LONG_TEXT_STYLE}>
					{stats.question || "—"}
				</div>
				<div className="text-muted font-size-sm mb-3">
					{stats.total} de {tablesCount} mesas han respondido
				</div>
				<OptionBars stats={stats} />
				<div className="mt-3 d-flex align-items-center flex-wrap">
					<span className="text-muted font-size-sm mr-2">Acierto:</span>
					<StateBadge color={stats.total ? COLORS.green : COLORS.gray}>
						{stats.correctCount} / {stats.total} · {stats.correctPct} %
					</StateBadge>
					{stats.fact && (
						<span className="text-muted font-size-sm ml-3">
							{stats.fact}
							{stats.source ? ` (${stats.source})` : ""}
						</span>
					)}
				</div>
			</div>
		</div>
	);
}

/** Tabla "respuestas de mesa por eje": nº de mesas con respuesta / mesas registradas. */
export function ResponsesByRound({ rounds, tablesCount }) {
	return (
		<table className="table table-sm table-vertical-center mb-0">
			<thead>
				<tr>
					<th className="border-top-0" style={{ width: "200px" }}>
						Eje
					</th>
					<th className="border-top-0">Pregunta</th>
					<th className="border-top-0 text-right" style={{ width: "140px" }}>
						Mesas con respuesta
					</th>
					<th className="border-top-0" style={{ width: "25%" }}></th>
				</tr>
			</thead>
			<tbody>
				{rounds.map(r => (
					<tr key={r.index}>
						<td className="font-weight-bolder">
							Eje {r.index} · {r.shortTitle}
						</td>
						<td className="text-muted font-size-sm" style={LONG_TEXT_STYLE}>
							{r.question || "—"}
						</td>
						<td className="text-right font-weight-bolder">
							{r.responses} / {tablesCount}
						</td>
						<td>
							<div className="d-flex align-items-center">
								<ProgressBar
									value={r.responsesPct}
									color={r.responses >= tablesCount && tablesCount ? COLORS.green : COLORS.blue}
									className="flex-grow-1 mr-2"
								/>
								<span className="font-size-sm" style={{ minWidth: "36px" }}>
									{r.responsesPct} %
								</span>
							</div>
						</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

/** Resumen de la encuesta: media por pregunta de escala, reparto de opción y texto libre. */
export function SurveySummary({ stats, compact }) {
	const max = stats.globalMax || 5;
	return (
		<>
			<div className="row">
				<KpiTile label="Respuestas" value={stats.total} col="col-6 col-md-3" />
				<KpiTile
					label="Media global (preguntas 1-5)"
					value={
						<>
							{stats.globalAvg != null ? stats.globalAvg : "—"}
							<span className="text-muted font-size-sm"> / {max}</span>
						</>
					}
					sub={`${stats.globalN} valoraciones`}
					col="col-6 col-md-3"
				/>
			</div>

			{stats.scale.length > 0 && (
				<div className="table-responsive mb-6">
					<table className="table table-sm table-vertical-center mb-0">
						<thead>
							<tr>
								<th className="border-top-0">Pregunta (escala 1-{max})</th>
								<th className="border-top-0 text-right" style={{ width: "70px" }}>
									n
								</th>
								<th className="border-top-0 text-right" style={{ width: "80px" }}>
									Media
								</th>
								<th className="border-top-0" style={{ width: compact ? "30%" : "40%" }}></th>
							</tr>
						</thead>
						<tbody>
							{stats.scale.map(s => (
								<tr key={s.key}>
									<td style={LONG_TEXT_STYLE}>{s.label}</td>
									<td className="text-right">{s.n}</td>
									<td className="text-right font-weight-bolder">
										{s.avg != null ? s.avg : "—"}
									</td>
									<td>
										<ProgressBar
											value={s.avg != null ? (s.avg / s.max) * 100 : 0}
											color={s.avg != null && s.avg >= s.max * 0.8 ? COLORS.green : COLORS.blue}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{stats.choice.length > 0 && (
				<div className="row">
					{stats.choice.map(c => (
						<div key={c.key} className="col-12 col-xl-6 mb-6">
							<div className="font-weight-bold mb-2" style={LONG_TEXT_STYLE}>
								{c.label}
							</div>
							<div className="text-muted font-size-sm mb-2">{c.n} respuesta(s)</div>
							{c.options.map(o => (
								<div key={o.option} className="d-flex align-items-center mb-1">
									<span style={{ minWidth: "90px" }}>{o.option}</span>
									<span className="font-weight-bolder mr-3" style={{ minWidth: "30px" }}>
										{o.count}
									</span>
									<ProgressBar value={o.pct} className="flex-grow-1 mr-2" />
									<span className="font-size-sm" style={{ minWidth: "36px" }}>
										{o.pct} %
									</span>
								</div>
							))}
						</div>
					))}
				</div>
			)}

			{stats.text.length > 0 && (
				<div className="mb-2">
					<div className="font-weight-bold mb-2">Preguntas de texto libre</div>
					{stats.text.map(t => (
						<div key={t.key} className="d-flex align-items-baseline mb-1">
							<StateBadge color={COLORS.lightBlue} className="mr-3">
								{t.n} con texto
							</StateBadge>
							<span className="text-muted font-size-sm" style={LONG_TEXT_STYLE}>
								{t.label}
							</span>
						</div>
					))}
				</div>
			)}
		</>
	);
}

/* ====================================================================== */
/* Pestaña Resumen del seguimiento de una sesión                           */
/* ====================================================================== */

export default function SummaryTab({ kpis, survey, onGoToTab }) {
	const { tables, phase } = kpis;
	return (
		<>
			{/* Estado general */}
			<div className="row">
				<KpiTile
					label="Fase actual"
					value={<span style={{ fontSize: "20px" }}>{phase.label}</span>}
					sub={`${phase.index} de ${phase.total} fases`}
				/>
				<KpiTile label="Ojo del Daruma" value={`${phase.eyeProgress} %`}>
					<ProgressBar value={phase.eyeProgress} height={8} className="mt-2" />
				</KpiTile>
				<KpiTile
					label="Mesas"
					value={
						<>
							{tables.registered}
							<span className="text-muted font-size-sm"> / {tables.planned} previstas</span>
						</>
					}
					sub={`${tables.connected} conectadas${
						tables.simulated ? ` · ${tables.simulated} simuladas` : ""
					}`}
				/>
				<KpiTile
					label="Pregunta de inicio"
					value={kpis.expectations.total}
					sub="respuestas recogidas por QR"
				/>
				<KpiTile
					label="Encuesta"
					value={
						<>
							{survey.total}
							{survey.globalAvg != null && (
								<span className="text-muted font-size-sm">
									{" "}
									· media {survey.globalAvg} / {survey.globalMax}
								</span>
							)}
						</>
					}
					sub="respuestas de satisfacción"
				/>
			</div>

			{/* Votación de apertura y dato clave por eje */}
			<h5 className="font-weight-bolder mt-4 mb-3">Votación de apertura y dato clave de cada eje</h5>
			<div className="row">
				<div className="col-12 col-xl-6 mb-4">
					<SurpriseSummaryCard
						title="Apertura"
						stats={kpis.opening.surprise}
						tablesCount={tables.registered}
						revealed={kpis.opening.revealed}
					/>
				</div>
				{kpis.rounds.map(r => (
					<div key={r.index} className="col-12 col-xl-6 mb-4">
						<SurpriseSummaryCard
							title={`Eje ${r.index} · ${r.shortTitle}`}
							stats={r.surprise}
							tablesCount={tables.registered}
							revealed={r.revealed}
						/>
					</div>
				))}
			</div>

			{/* Respuestas de mesa por eje */}
			<div className="d-flex align-items-center justify-content-between mt-4 mb-3">
				<h5 className="font-weight-bolder mb-0">
					Respuestas de mesa por eje{" "}
					<span className="text-muted font-size-sm font-weight-normal ml-2">
						{kpis.totalResponses} en total
					</span>
				</h5>
				{onGoToTab && (
					<button
						type="button"
						className="btn btn-sm btn-light-primary font-weight-bold"
						onClick={() => onGoToTab("responses")}
					>
						Ver todas las respuestas
					</button>
				)}
			</div>
			<div className="table-responsive mb-6">
				<ResponsesByRound rounds={kpis.rounds} tablesCount={tables.registered} />
			</div>

			{/* Pregunta de inicio */}
			<h5 className="font-weight-bolder mt-4 mb-1">Pregunta de inicio (expectativas)</h5>
			<div className="font-weight-bold" style={LONG_TEXT_STYLE}>
				{kpis.expectations.question || "—"}
			</div>
			<div className="text-muted font-size-sm mb-6">
				{kpis.expectations.total} respuesta(s)
				{onGoToTab && (
					<button
						type="button"
						className="btn btn-sm btn-link p-0 ml-3"
						onClick={() => onGoToTab("expectations")}
					>
						Ver la lista
					</button>
				)}
			</div>

			{/* Encuesta */}
			<div className="d-flex align-items-center justify-content-between mt-4 mb-3">
				<h5 className="font-weight-bolder mb-0">Encuesta de satisfacción</h5>
				{onGoToTab && (
					<button
						type="button"
						className="btn btn-sm btn-light-primary font-weight-bold"
						onClick={() => onGoToTab("survey")}
					>
						Ver respuestas
					</button>
				)}
			</div>
			<SurveySummary stats={survey} />
		</>
	);
}
